import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Megaphone, PenLine, Quote } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("question");
  const [q, setQ] = useState({ zone: "casual", answer: "", clue: "" });
  const [shout, setShout] = useState("");
  const [shoutouts, setShoutouts] = useState([]);
  const [mine, setMine] = useState([]);

  const load = () => {
    api.get("/community/shoutouts").then(({ data }) => setShoutouts(data.shoutouts)).catch(() => {});
    if (user) api.get("/community/my-submissions").then(({ data }) => setMine(data.submissions)).catch(() => {});
  };
  useEffect(load, [user]);

  const submitQuestion = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    try {
      await api.post("/community/submit", { type: "question", zone: q.zone, answer: q.answer, clue: q.clue });
      toast.success("Question submitted for admin review!");
      setQ({ zone: "casual", answer: "", clue: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitShout = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    try {
      await api.post("/community/submit", { type: "shoutout", content: shout });
      toast.success("Shout out submitted for review!");
      setShout("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
        <p className="font-mono-caps text-xs text-primary mb-3">Community</p>
        <h1 className="font-display text-5xl mb-8">GET INVOLVED</h1>

        <div className="flex flex-wrap gap-2 mb-8">
          {[["question", "Submit Question", PenLine], ["shoutout", "Shout Out", Megaphone], ["wall", "Shout-Out Wall", Quote], ["mine", "My Submissions", null]].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`community-tab-${id}`}
              className={`flex items-center gap-1.5 font-mono-caps text-[10px] px-4 py-2.5 border transition-colors ${tab === id ? "bg-primary text-primary-foreground border-primary" : "border-white/20 text-muted-foreground hover:border-primary"}`}>
              {Icon && <Icon size={13} />} {label}
            </button>
          ))}
        </div>

        {tab === "question" && (
          <form onSubmit={submitQuestion} className="border border-white/15 bg-card p-6 space-y-4 max-w-xl" data-testid="submit-question-form">
            <p className="text-sm text-muted-foreground">Suggest a clue & answer. Approved entries can become puzzle content.</p>
            <select value={q.zone} onChange={(e) => setQ({ ...q, zone: e.target.value })} data-testid="q-zone" className="w-full bg-background border border-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary">
              <option value="casual">New Jack</option><option value="golden_era">Triple OG</option>
            </select>
            <input required value={q.answer} onChange={(e) => setQ({ ...q, answer: e.target.value })} placeholder="ANSWER (letters only)" data-testid="q-answer" className="w-full bg-background border border-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary uppercase" />
            <textarea required value={q.clue} onChange={(e) => setQ({ ...q, clue: e.target.value })} rows={3} placeholder="Write the clue…" data-testid="q-clue" className="w-full bg-background border border-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary resize-none" />
            <button type="submit" data-testid="q-submit" className="bg-primary text-primary-foreground font-mono-caps text-xs px-6 py-3 border border-primary hover-lift">Submit Question</button>
          </form>
        )}

        {tab === "shoutout" && (
          <form onSubmit={submitShout} className="border border-white/15 bg-card p-6 space-y-4 max-w-xl" data-testid="shoutout-form">
            <p className="text-sm text-muted-foreground">Give a shout to your city, your crew, or your favorite MC. Approved shouts appear on the wall.</p>
            <textarea required value={shout} onChange={(e) => setShout(e.target.value)} rows={3} placeholder="Big up to…" data-testid="shout-content" className="w-full bg-background border border-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary resize-none" />
            <button type="submit" data-testid="shout-submit" className="bg-primary text-primary-foreground font-mono-caps text-xs px-6 py-3 border border-primary hover-lift">Send Shout Out</button>
          </form>
        )}

        {tab === "wall" && (
          <div className="space-y-4" data-testid="shoutout-wall">
            {shoutouts.length === 0 ? <p className="text-muted-foreground text-sm">No shout-outs approved yet.</p> :
              shoutouts.map((s, i) => (
                <div key={i} className="border-l-2 border-primary bg-card p-5">
                  <Quote className="text-primary mb-2" size={16} />
                  <p className="text-lg">{s.content}</p>
                  <p className="font-mono-caps text-[9px] text-muted-foreground mt-2">— {s.user_name}</p>
                </div>
              ))}
          </div>
        )}

        {tab === "mine" && (
          <div className="border border-white/15 bg-card" data-testid="my-submissions">
            {mine.length === 0 ? <p className="text-muted-foreground text-sm p-6">You haven't submitted anything yet.</p> :
              mine.map((s) => (
                <div key={s.submission_id} className="flex items-center justify-between px-5 py-3.5 border-b border-white/5">
                  <div><span className="font-mono-caps text-[9px] text-secondary mr-2">{s.type}</span>{s.answer || s.content?.slice(0, 40) || s.clue?.slice(0, 40)}</div>
                  <span className={`font-mono-caps text-[9px] ${s.status === "approved" ? "text-secondary" : s.status === "rejected" ? "text-destructive" : "text-muted-foreground"}`}>{s.status}</span>
                </div>
              ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
